import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import spottoLogo from '../../assets/spotto-logo.png';

const BUSINESS_APP_URL = 'https://business.spotto.pl';

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const current = i18n.language.startsWith('en') ? 'en' : 'pl';
  const toggleTo = current === 'pl' ? 'en' : 'pl';

  return (
    <button
      onClick={() => i18n.changeLanguage(toggleTo)}
      className="font-mono text-xs tracking-[0.2em] text-ink-on-dark-muted hover:text-ink-on-dark transition"
      aria-label={`Switch language to ${toggleTo.toUpperCase()}`}
    >
      {current === 'pl' ? 'EN' : 'PL'}
    </button>
  );
}

/**
 * Spotto top navigation header — used on landing and all sub-pages
 * (privacy policy, regulations, contact). Fixed + glassmorphism style.
 *
 * Anchor links (#features, #partners, #about) auto-resolve to landing
 * regardless of current route: on `/` they scroll, off-landing they
 * navigate to `/#anchor`.
 */
export function SpottoHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const onLanding = location.pathname === '/';

  const anchor = (id: string) => (onLanding ? `#${id}` : `/#${id}`);

  return (
    <header className="fixed inset-x-0 top-0 z-50 backdrop-blur bg-surface-dark/85 border-b border-white/[0.06]">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" aria-label="Spotto — strona główna">
          <img src={spottoLogo} alt="Spotto" className="h-10 w-10 rounded-full logo-glow" />
          <span className="font-display text-[1.4rem] font-light tracking-tight text-ink-on-dark">
            Spotto
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm text-ink-on-dark-muted">
          <a href={anchor('features')} className="hover:text-ink-on-dark transition">
            {t('landing.nav.features')}
          </a>
          <a href={anchor('partners')} className="hover:text-ink-on-dark transition">
            {t('landing.nav.partners')}
          </a>
          <a href={anchor('about')} className="hover:text-ink-on-dark transition">
            {t('landing.nav.about')}
          </a>
          <a
            href="https://help.spotto.pl"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-ink-on-dark transition inline-flex items-center gap-1"
          >
            {t('landing.nav.help')}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-3 h-3 opacity-60"
              aria-hidden="true"
            >
              <path d="M7 17L17 7M17 7H8M17 7V16" />
            </svg>
          </a>
        </nav>
        <div className="flex items-center gap-5">
          <LanguageSwitcher />
          <a
            href={`${BUSINESS_APP_URL}/login`}
            className="text-sm font-semibold px-4 py-2 rounded-lg bg-brand-primary hover:bg-brand-primary-hover text-white transition"
          >
            {t('landing.nav.login')}
          </a>
        </div>
      </div>
    </header>
  );
}
